import React from 'react';
import {
  Box,
  ButtonBase,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AutoAwesomeRounded as AutoAwesomeIcon,
  SearchRounded as SearchIcon,
} from '@mui/icons-material';
import colors from '../../assets/theme/colors';
import {
  ASK_ACCENT,
  ASK_ACCENT_DARK,
  ASK_SAGE_TINT,
  ASK_TINT,
} from './askRecordUi';

const AskRecordLauncherCard = ({
  firstName,
  accent,
  logCount = 0,
  onClick,
  variant = 'default',
  sx,
}) => {
  const isToolsHero = variant === 'toolsHero';
  const activeAccent = accent || (isToolsHero ? {
    primary: colors.semantic.sageAction,
    ink: colors.semantic.sageActionInk,
    soft: colors.semantic.sageSoft,
    wash: colors.semantic.sageMist,
  } : {
    primary: ASK_ACCENT,
    ink: ASK_ACCENT_DARK,
    soft: ASK_TINT,
    wash: ASK_SAGE_TINT,
  });
  const accentPrimary = activeAccent.primary || ASK_ACCENT;
  const accentInk = activeAccent.ink || accentPrimary;
  const accentSoft = activeAccent.soft || alpha(accentPrimary, 0.14);
  const accentWash = activeAccent.wash || alpha(accentPrimary, 0.06);

  return (
  <ButtonBase
    component="div"
    role="button"
    aria-label={`Ask about ${firstName}'s care`}
    onClick={onClick}
    sx={{
      width: '100%',
      display: 'block',
      textAlign: 'left',
      borderRadius: '24px',
      position: 'relative',
      overflow: 'hidden',
      isolation: 'isolate',
      p: '1.5px',
      transition: 'transform 160ms ease',
      '@keyframes askRecordNeonFlow': {
        to: {
          transform: 'rotate(1turn)',
        },
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: '-75%',
        zIndex: 0,
        pointerEvents: 'none',
        background: isToolsHero
          ? `linear-gradient(135deg, ${alpha(accentPrimary, 0.2)}, ${alpha(accentInk, 0.12)})`
          : `conic-gradient(from 0deg, ${alpha('#62E6F7', 0.5)}, ${alpha('#8B5CF6', 0.42)}, ${alpha('#F472B6', 0.36)}, ${alpha('#F8D66D', 0.32)}, ${alpha('#35D399', 0.36)}, ${alpha('#62E6F7', 0.5)})`,
        animation: isToolsHero ? 'none' : 'askRecordNeonFlow 9s linear infinite',
        filter: isToolsHero ? 'none' : `drop-shadow(0 0 4px ${alpha(accentPrimary, 0.18)})`,
        willChange: 'transform',
      },
      '@media (prefers-reduced-motion: reduce)': {
        '&::before': {
          animation: 'none',
        },
      },
      '&:focus-visible': {
        outline: `3px solid ${alpha(accentPrimary, 0.28)}`,
        outlineOffset: 3,
      },
      '&:hover': {
        transform: 'translateY(-1px)',
      },
      ...sx,
    }}
  >
    <Box
      sx={{
        position: 'relative',
        zIndex: 1,
        overflow: 'hidden',
        p: isToolsHero ? { xs: 1.35, sm: 1.5 } : { xs: 1.05, sm: 1.2 },
        borderRadius: '22.5px',
        bgcolor: '#FFFFFF',
        background: isToolsHero
          ? `linear-gradient(135deg, #FFFFFF 0%, ${alpha(accentWash, 0.88)} 58%, ${alpha(accentSoft, 0.72)} 100%)`
          : `linear-gradient(135deg, ${colors.semantic.profileFlow.cardBg} 0%, ${ASK_TINT} 58%, ${ASK_SAGE_TINT} 100%)`,
        border: `1px solid ${alpha(accentPrimary, isToolsHero ? 0.08 : 0.08)}`,
        boxShadow: 'none',
        transition: 'border-color 160ms ease',
        '&:hover': {
          borderColor: alpha(accentPrimary, 0.12),
        },
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: isToolsHero ? 1 : 0.85 }}>
        <Box
          sx={{
            width: isToolsHero ? { xs: 42, sm: 46 } : { xs: 38, sm: 42 },
            height: isToolsHero ? { xs: 42, sm: 46 } : { xs: 38, sm: 42 },
            borderRadius: isToolsHero ? '16px' : '999px',
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(accentPrimary, isToolsHero ? 0.1 : 0.12),
            color: accentInk,
            border: `1px solid ${alpha(accentPrimary, 0.12)}`,
            boxShadow: isToolsHero ? 'none' : `0 8px 18px ${alpha(accentPrimary, 0.1)}`,
            flexShrink: 0,
          }}
        >
          {isToolsHero ? (
            <SearchIcon sx={{ fontSize: { xs: 22, sm: 24 } }} />
          ) : (
            <AutoAwesomeIcon sx={{ fontSize: { xs: 19, sm: 21 } }} />
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: isToolsHero ? { xs: '1.08rem', sm: '1.2rem' } : { xs: '0.98rem', sm: '1.08rem' }, fontWeight: 950, color: colors.semantic.ink, lineHeight: 1.04 }}>
            Ask about {firstName}&apos;s care
          </Typography>
          <Typography sx={{ mt: 0.25, fontSize: { xs: '0.78rem', sm: '0.86rem' }, color: alpha(colors.semantic.ink, 0.62), lineHeight: 1.15, fontWeight: 500 }}>
            Anything across {firstName}&apos;s saved care notes
            {logCount > 0 ? ` · ${Number(logCount).toLocaleString()} logs` : ''}
          </Typography>
        </Box>
      </Box>
      {isToolsHero ? (
        <>
          <Box
            sx={{
              mt: 1.25,
              px: 1,
              py: 0.8,
              borderRadius: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: 0.65,
              bgcolor: alpha('#FFFFFF', 0.86),
              border: `1px solid ${alpha(accentPrimary, 0.08)}`,
              color: alpha(colors.semantic.ink, 0.52),
              fontSize: { xs: '0.9rem', sm: '0.98rem' },
              fontWeight: 500,
            }}
          >
            <SearchIcon sx={{ fontSize: 18, color: alpha(colors.semantic.ink, 0.48) }} />
            &ldquo;When did sleep change?&rdquo;
          </Box>
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.55 }}>
            {['Sleep this week', 'Recent responses', 'Med schedule'].map((label) => (
              <Box
                key={label}
                component="span"
                sx={{
                  px: 0.85,
                  py: 0.4,
                  borderRadius: '999px',
                  bgcolor: alpha(accentPrimary, 0.1),
                  color: accentInk,
                  fontSize: '0.78rem',
                  fontWeight: 800,
                  lineHeight: 1,
                }}
              >
                {label}
              </Box>
            ))}
          </Box>
        </>
      ) : null}
    </Box>
  </ButtonBase>
  );
};

export default AskRecordLauncherCard;
